/**
 * Where a gradient band stops when the data goes silent.
 *
 * A band used to run to the NEXT reading, however far away. So an 11-day
 * outage (all four Shelly devices at one client, 26 Apr–6 May 2026) painted
 * the plug's power lane solid, as if it had readings, while the temperature
 * lane showed grey only because its last pre-gap reading happened to be null
 * (Ed, 2026-09-25). A silence longer than SILENCE_MS now ends the band and
 * leaves the lane background showing: no colour means no data.
 *
 * Gradient lanes only. State lanes emit ON CHANGE, so a long silence there is
 * "nothing changed" and still forward-fills (OMON-232).
 */

/** Silence longer than this is a gap. Three hourly archive buckets missing in
 *  a row; well above the minute-level cadence the Shelly and hub sensors keep. */
export const SILENCE_MS = 3 * 60 * 60 * 1000;

/** Does the step from `t0` to `t1` (ms) count as a gap? */
export function isSilence(t0: number, t1: number): boolean {
  return t1 - t0 > SILENCE_MS;
}

/**
 * End (ms) of the band for a reading at `t` that is followed by a gap. It
 * covers one step of the lane's usual spacing (the step in from `prevT`),
 * capped at SILENCE_MS, so the last reading before an outage stays visible
 * without claiming the outage. With no previous reading, one SILENCE_MS.
 */
export function bandEndBeforeGap(t: number, prevT: number | null): number {
  const step = prevT === null ? SILENCE_MS : t - prevT;
  return t + Math.min(Math.max(step, 0), SILENCE_MS);
}
